<script setup>
import {readonly, ref, nextTick, computed} from "vue"
import axios from "axios"
import {onMounted} from "vue"
import {useChatGptStore} from "@/stores/chatGpt.js";
import AutoComplete from 'primevue/autocomplete'
import Breadcrumbs from "@/components/Breadcrumbs/index.vue";
import {Icon} from "@iconify/vue";
import FloatLabel from "primevue/floatlabel";
import {InputText, Textarea} from "primevue";
import {showMessage} from "@/utils/showMessage.js";
import {useVuelidate} from "@vuelidate/core";
import {required} from "@vuelidate/validators";

const responseText = ref("");
let responseChat = ref("");
const loading = ref(false);
const searching = ref(false)
const gettingChatData = ref(false)
const channelId = ref("");
const userName = ref("");
const prompt = ref("");
const contactList = ref([]);

const chatGptStore = useChatGptStore();

const rulesForCallGPT = computed(() => {
  return {required};
});
const $vuelidateCallGPT = useVuelidate(rulesForCallGPT, prompt);

const rulesForChatData = computed(() => {
  return {required};
});
const $vuelidateChatData = useVuelidate(rulesForChatData, channelId);
const callApiChatGpt = async (prompt) => {

  /*if (!prompt || !prompt.trim()) {
    console.warn("⚠️ prmpt خالی است، اجازه ارسال نیست!");
    return;
  }*/

  $vuelidateCallGPT.value.$touch();
  if ($vuelidateCallGPT.value.$invalid) {
    return false;
  }

  prompt = prompt.replace(/\r?\n|\r/g, "");

  loading.value = true
  responseText.value = ""

  const API_URL = "https://api.openai.com/v1/chat/completions"

  const API_KEY = "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" // 🔒 کلید API خودت رو اینجا بذار

  try {
    const res = await axios.post(
        API_URL,
        {
          model: "gpt-5",
          messages: [
            {
              role: "user",
              content: prompt
            }
          ]
        },
        {
          headers: {
            "Authorization": `Bearer ${API_KEY}`,
            "Content-Type": "application/json"
          },
          timeout: 300000
        }
    )

    responseText.value = res.data?.choices?.[0]?.message?.content || "No response received from the server."
  } catch (err) {
    showMessage(err, "error", 1500)
    //responseText.value = "⚠️ خطا در ارتباط با سرور"
  } finally {
    loading.value = false
  }
}

const callApiGetUserChatHistory = async () => {
  $vuelidateChatData.value.$touch();
  if ($vuelidateChatData.value.$invalid) {
    return false;
  }
  responseChat.value = "";
  gettingChatData.value = true
  try {
    await chatGptStore.action_get_chat(channelId.value);
    responseChat.value = JSON.stringify(chatGptStore.get_chat_data, null, 2);
  } catch (err) {
    showMessage(err, 'error')
  } finally {
    gettingChatData.value = false
  }
}

const callApiGetChannelIdByUserName = async (event) => {
  const keyword = event.query?.trim()
  if (!keyword || keyword.length < 3) {
    contactList.value = []
    searching.value = false
    return
  }
  contactList.value = []
  searching.value = true
  try {
    await chatGptStore.action_get_data(keyword);
    const data = chatGptStore.get_chat_filtered_data;
    contactList.value = data.map(item => ({
      label: `${item.contact} - ${(item.store).split(" ")[0]}`,
      value: item.channel
    }));
  } finally {
    searching.value = false
  }
}


const onSelectContact = (e) => {
  const selectedItem = e.value;
  channelId.value = selectedItem.value;
};

const copyResponseChat = async () => {
  try {
    await navigator.clipboard.writeText(responseChat.value)
    showMessage("copied", "success", 1500)
  } catch (err) {
    console.error('Copy failed:', err)
    showMessage("Failed to copy!", "error", 1500)
  }
}

const pasteResponseChat = async () => {
  try {
    const text = await navigator.clipboard.readText()
    if (!text) return

    // find the prompt textarea element by id
    const textarea = document.getElementById('prompt')
    if (!textarea) return

    // get cursor position
    const start = textarea.selectionStart
    const end = textarea.selectionEnd

    // insert the text at the cursor
    const before = prompt.value.substring(0, start)
    const after = prompt.value.substring(end)
    prompt.value = before + text + after

    // move cursor to after inserted text
    nextTick(() => {
      textarea.selectionStart = textarea.selectionEnd = start + text.length
      textarea.focus()
    })

    showMessage("pasted", "success", 1500)
  } catch (err) {
    console.error('Paste failed:', err)
    showMessage("failed to paste", "error", 1500)
  }
}
</script>

<template>
  <div class="h-full bg-slate-900">
    <div class="flex flex-col gap-4 w-full h-full overflow-hidden">
      <div class="flex-1">
        <div class="flex flex-col gap-4 h-full">
          <div class="flex-1 grid grid-cols-6 gap-4 h-full pt-2">
            <div class="col-span-4 flex flex-col gap-4 h-full">
              <div class="flex flex-row items-center gap-4 w-full">
                <div class="flex-1 flex flex-row items-center gap-2">
                  <FloatLabel class="flex-1 custom-select" variant="on">
                    <AutoComplete v-model="userName" input-id="contactName" :spellcheck="false"
                                  :suggestions="contactList" :forceSelection="true" :showClear="true"
                                  @complete="callApiGetChannelIdByUserName"
                                  completeOnFocus
                                  optionLabel="label" @itemSelect="onSelectContact" :minLength="3" :delay="1000"
                                  :loading="searching" class="w-full !rounded-md"
                                  input-class="w-full min-w-28 !bg-transparent h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"
                                  overlay-class="!bg-[#0F172A] !rounded-md" append-to="self"
                                  :virtualScrollerOptions="{ itemSize: 48 }"
                                  dropdow :readonly="readonly">
                      <template #empty>
                        <div v-if="searching" class="flex items-center justify-center py-8">
                          <Icon icon="eos-icons:bubble-loading" class="size-8"/>
                        </div>
                        <p v-else-if="userName && userName.length > 2" class="text-slate-400 text-center">
                          No result
                        </p>
                        <p v-else class="text-slate-400 text-center">Enter contact name</p>
                      </template>
                    </AutoComplete>
                    <label for="contactName" class="!bg-slate-900 !text-xs 2xl:!text-sm">Contact Name</label>
                  </FloatLabel>
                  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none"
                       stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                       class="lucide lucide-arrow-right-to-line-icon lucide-arrow-right-to-line">
                    <path d="M17 12H3"/>
                    <path d="m11 18 6-6-6-6"/>
                    <path d="M21 5v14"/>
                  </svg>
                  <FloatLabel class="flex-1" variant="on">
                    <InputText id="channelID" v-model="channelId" autocomplete="off"
                               class="!bg-transparent w-full rounded-md h-[32px] 2xl:h-[40px] !text-xs 2xl:!text-sm !border-slate-700"
                               :class="{ 'has-error': $vuelidateChatData.$error }"/>
                    <label for="channelID" class="!bg-slate-900 !text-xs 2xl:!text-sm"
                           :class="{ 'focus:text-red-300': $vuelidateChatData.$error }">Channel ID</label>
                  </FloatLabel>
                </div>
                <button
                    @click="callApiGetUserChatHistory"
                    :disabled="gettingChatData"
                    class="border border-slate-700 text-white !text-xs 2xl:!text-sm px-5 h-[32px] 2xl:h-[40px] rounded-md w-max hover:bg-slate-800 disabled:opacity-50 flex flex-row gap-2 items-center">
                  <svg v-if="gettingChatData" xmlns="http://www.w3.org/2000/svg" width="16" height="16"
                       viewBox="0 0 24 24"
                       fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                       class="lucide lucide-loader-icon lucide-loader animate-[spin_2s_linear_infinite]">
                    <path d="M12 2v4"/>
                    <path d="m16.2 7.8 2.9-2.9"/>
                    <path d="M18 12h4"/>
                    <path d="m16.2 16.2 2.9 2.9"/>
                    <path d="M12 18v4"/>
                    <path d="m4.9 19.1 2.9-2.9"/>
                    <path d="M2 12h4"/>
                    <path d="m4.9 4.9 2.9 2.9"/>
                  </svg>
                  <svg v-else xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"
                       stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                       class="lucide lucide-message-circle-code-icon lucide-message-circle-code">
                    <path d="m10 9-3 3 3 3"/>
                    <path d="m14 15 3-3-3-3"/>
                    <path
                        d="M2.992 16.342a2 2 0 0 1 .094 1.167l-1.065 3.29a1 1 0 0 0 1.236 1.168l3.413-.998a2 2 0 0 1 1.099.092 10 10 0 1 0-4.777-4.719"/>
                  </svg>
                  Get Chat Data
                </button>
              </div>
              <div class="flex-1 grid grid-rows-2 gap-4 h-full">
                <div class="flex flex-col h-full gap-0.5 text-xs 2xl:text-sm relative">
                  <FloatLabel variant="on" class="h-full">
                  <Textarea v-auto-dir id="description" v-model="responseChat"
                            class="!bg-transparent w-full h-full rounded-md !text-xs 2xl:!text-sm !border-slate-700 resize-none"/>
                    <label for="description" class="!bg-slate-900">Chat Data</label>
                  </FloatLabel>

                  <button @click="copyResponseChat" class="absolute right-5 top-5">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                         stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                         class="lucide lucide-copy-icon lucide-copy">
                      <rect width="14" height="14" x="8" y="8" rx="2" ry="2"/>
                      <path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/>
                    </svg>
                  </button>
                </div>
                <div class="flex flex-col h-full gap-0.5 text-xs 2xl:text-sm relative">
                  <FloatLabel variant="on" class="h-full" :class="{ 'has-error': $vuelidateCallGPT.$error }">
                    <Textarea v-auto-dir id="prompt"
                              v-model="prompt"
                              class="!bg-transparent w-full h-full rounded-md !text-xs 2xl:!text-sm !border-slate-700 resize-none"
                              :class="['!bg-transparent w-full h-full rounded-md !text-xs 2xl:!text-sm !border-slate-700 resize-none',
                                     { 'has-error': $vuelidateCallGPT.$error }
                                   ]"
                    />
                    <label for="prompt" class="!bg-slate-900">Prompt</label>
                  </FloatLabel>
                  <button @click="pasteResponseChat" class="absolute right-5 top-5">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none"
                         stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                         class="lucide lucide-clipboard-icon lucide-clipboard">
                      <rect width="8" height="4" x="8" y="2" rx="1" ry="1"/>
                      <path d="M16 4h2a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h2"/>
                    </svg>
                  </button>
                </div>
              </div>
            </div>
            <div class="col-span-2 flex flex-col gap-2 text-slate-400 h-full">
              <FloatLabel variant="on" class="h-full">
                <Textarea v-auto-dir id="prompt"
                          :value="responseText"
                          class="!bg-transparent w-full h-full rounded-md !text-xs 2xl:!text-sm !border-slate-700 resize-none"
                          :class="{'p-filled': !!responseText }"
                />
                <label for="prompt" class="!bg-slate-900 !text-xs 2xl:!text-sm">Result</label>
              </FloatLabel>
            </div>
          </div>


          <button
              @click="() => callApiChatGpt(prompt)"
              :disabled="loading"
              class="border border-slate-700 text-white !text-xs 2xl:!text-sm px-5 h-[32px] 2xl:h-[40px] rounded-md w-max hover:bg-slate-800 disabled:opacity-50 flex flex-row gap-2 items-center">
            <svg v-if="loading" xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24"
                 fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                 class="lucide lucide-loader-icon lucide-loader animate-[spin_2s_linear_infinite]">
              <path d="M12 2v4"/>
              <path d="m16.2 7.8 2.9-2.9"/>
              <path d="M18 12h4"/>
              <path d="m16.2 16.2 2.9 2.9"/>
              <path d="M12 18v4"/>
              <path d="m4.9 19.1 2.9-2.9"/>
              <path d="M2 12h4"/>
              <path d="m4.9 4.9 2.9 2.9"/>
            </svg>
            <svg v-else xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none"
                 stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                 class="lucide lucide-send-horizontal-icon lucide-send-horizontal">
              <path
                  d="M3.714 3.048a.498.498 0 0 0-.683.627l2.843 7.627a2 2 0 0 1 0 1.396l-2.842 7.627a.498.498 0 0 0 .682.627l18-8.5a.5.5 0 0 0 0-.904z"/>
              <path d="M6 12h16"/>
            </svg>
            Send To GPT
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<!--<style scoped>

:deep(.custom-input) {
  width: 100%;
  padding: 12px 16px;
  background: rgba(26, 32, 44, 0.85);
  backdrop-filter: blur(8px);
  border: 1px solid #4a5568;
  border-radius: 12px;
  color: #e2e8f0;
  font-size: 14px;
  transition: all 0.25s ease;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.3);
}

:deep(.custom-input:hover) {
  border-color: #718096;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.35);
}

:deep(.custom-input:focus) {
  outline: none;
  border-color: #4299e1;
  box-shadow: 0 0 0 4px rgba(66, 153, 225, 0.3);
  transform: translateY(-1px);
}


:deep(.custom-panel) {
  background: rgba(26, 32, 44, 0.95);
  backdrop-filter: blur(10px);
  border: 1px solid #4a5568 !important;
  border-radius: 12px !important;
  margin-top: 4px !important;
  box-shadow: 0 12px 28px rgba(0, 0, 0, 0.45) !important;
  overflow: hidden !important;
  padding: 0 !important;
  animation: dropdownFade 0.2s ease-out;
}

</style>-->
